// Desenho do céu e da luz no canvas. Cada efeito é pré-desenhado uma vez e guardado: quadro a
// quadro sobra um drawImage. A camada do sol de uma sala só é refeita quando a hora anda 5 min.
//
// A lógica (quanto, para onde, de que cor) está em ceu.ts; aqui é só pintura.

import { mapSize, TILE } from '../../core/world.js';
import { bake, ctx2d, makeCanvas } from '../engine/bake.js';
import type { BuiltRoom } from '../engine/room.js';
import type { PixelSprite } from '../art/types.js';
import { feixesDaJanela, janelasDaSala, raioDoSol } from './ceu.js';

/** Linha do quadro do personagem onde o chapéu encosta (a testa; os olhos são as linhas 6–7). */
const TESTA = 5;

const camadas = new WeakMap<BuiltRoom, { chave: number; canvas: HTMLCanvasElement | null }>();

/**
 * Raios de sol que entram pelas janelas da sala, na origem do piso (desenhe em ox, oy). Null à noite
 * ou sem janela. Refeita só quando a hora muda 5 min (a chave), e some junto com a sala apagada.
 */
export function camadaDoSol(sala: BuiltRoom, hora: number): HTMLCanvasElement | null {
  const chave = Math.round(hora * 12);
  const hit = camadas.get(sala);
  if (hit && hit.chave === chave) return hit.canvas;
  const canvas = pintaSol(sala, hora);
  camadas.set(sala, { chave, canvas });
  return canvas;
}

function pintaSol(sala: BuiltRoom, hora: number): HTMLCanvasElement | null {
  const raio = raioDoSol(hora);
  const janelas = janelasDaSala(sala.map);
  if (!raio || janelas.length === 0) return null;
  const { w, h } = mapSize(sala.map);
  const canvas = makeCanvas(w * TILE, h * TILE);
  const ctx = ctx2d(canvas);
  const [r, g, b] = raio.cor;
  for (const janela of janelas) {
    // a janela tem 30 px dentro dos 32 px do móvel (room.ts centraliza)
    const x0 = janela.x * TILE + 1;
    const x1 = (janela.x + janela.w) * TILE - 1;
    for (const feixe of feixesDaJanela(x0, x1, raio)) {
      // a luz é forte logo abaixo da janela e some ao longe
      const grad = ctx.createLinearGradient(0, 0, 0, raio.comprimento);
      grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${raio.alfa * feixe.fator})`);
      grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      feixe.pontos.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)));
      ctx.closePath();
      ctx.fill();
    }
  }
  return canvas;
}

const brilhos = new Map<string, HTMLCanvasElement>();

/**
 * Brilho redondo pré-desenhado, de `raio` px de meio-lado. Cache por cor e raio (são poucos):
 * desenhar é um drawImage, de preferência com a composição 'lighter'.
 */
export function brilho(cor: string, raio: number): HTMLCanvasElement {
  const chave = `${cor}|${raio}`;
  const hit = brilhos.get(chave);
  if (hit) return hit;
  const canvas = makeCanvas(raio * 2, raio * 2);
  const ctx = ctx2d(canvas);
  const grad = ctx.createRadialGradient(raio, raio, 0, raio, raio, raio);
  grad.addColorStop(0, cor);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, raio * 2, raio * 2);
  brilhos.set(chave, canvas);
  return canvas;
}

/**
 * Chapéu da época sobre a cabeça. `topo` é o canto de cima do quadro do personagem (16×24) e `dy`
 * o deslocamento da cabeça naquela pose (sentado ou andando): o chapéu acompanha a cabeça.
 */
export function desenhaChapeu(c: CanvasRenderingContext2D, sprite: PixelSprite, x: number, topo: number, dy: number): void {
  const img = bake(sprite);
  c.drawImage(img, Math.round(x - img.width / 2), topo + dy + TESTA - img.height + 1);
}
